// Pagina prenotazione manutenzione programmata portale.
// Selettore mese, griglia calendario slot disponibili, conferma prenotazione.
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Calendar, CheckCircle, ArrowLeft, Info } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Alert } from '../../components/ui/Alert'
import { Card } from '../../components/ui/Card'
import { Select } from '../../components/ui/Select'
import { Skeleton } from '../../components/ui/Skeleton'
import { SlotCalendar } from './components/SlotCalendar'
import { portalApiCalls, portalApi } from './portal.api'
import { usePortalStore } from './portal.store'
import { formatDate } from '../../lib/utils'

interface Contract {
  id: string
  contractNumber: string
  title: string
  status: string
}

export function PortalCalendarPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const companyId = usePortalStore((s) => s.companyId)

  const now = new Date()
  const [currentMonth, setCurrentMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  )
  const [selectedContractId, setSelectedContractId] = useState<string>('')
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [bookingNotes, setBookingNotes] = useState('')
  const [bookingConfirmed, setBookingConfirmed] = useState<{
    ticketNumber: string
    scheduledStart: string
  } | null>(null)

  // Carica contratti attivi dell'azienda
  const { data: contracts, isLoading: contractsLoading } = useQuery({
    queryKey: ['portal', 'contracts', companyId],
    queryFn: async () => {
      const res = await portalApi.get<Contract[]>(
        `/../../contracts?companyId=${companyId}&status=active&limit=50`
      )
      return res.data
    },
    enabled: Boolean(companyId),
    placeholderData: [],
  })

  const contractOptions = (contracts ?? []).map((c) => ({
    value: c.id,
    label: `${c.contractNumber} — ${c.title}`,
  }))

  // Carica slot disponibili per il contratto e mese selezionati
  const { data: slots, isLoading: slotsLoading } = useQuery({
    queryKey: ['portal', 'slots', selectedContractId, currentMonth],
    queryFn: () =>
      portalApiCalls
        .getAvailableSlots(selectedContractId, currentMonth)
        .then((r) => r.data),
    enabled: Boolean(selectedContractId),
    placeholderData: [],
  })

  // Mutation prenotazione slot
  const bookMutation = useMutation({
    mutationFn: () =>
      portalApiCalls.bookSlot({
        contractId: selectedContractId,
        date: selectedDate!,
        notes: bookingNotes || undefined,
      }),
    onSuccess: ({ data }) => {
      setBookingConfirmed({
        ticketNumber: data.ticket.ticketNumber,
        scheduledStart: data.ticket.scheduledStart,
      })
      void queryClient.invalidateQueries({ queryKey: ['portal', 'slots'] })
      void queryClient.invalidateQueries({ queryKey: ['portal', 'dashboard'] })
    },
  })

  const bookingError = bookMutation.error
    ? bookMutation.error instanceof Error
      ? bookMutation.error.message
      : 'Errore durante la prenotazione. Riprova.'
    : null

  // Stato: prenotazione confermata
  if (bookingConfirmed) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center py-20 text-center">
        <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-50">
          <CheckCircle className="h-12 w-12 text-[var(--fc-success)]" aria-hidden="true" />
        </div>
        <h1 className="mb-2 text-2xl font-bold text-gray-900">Manutenzione prenotata!</h1>
        <p className="mb-1 text-gray-500">
          La manutenzione programmata per il{' '}
          <strong>{formatDate(bookingConfirmed.scheduledStart, 'dd/MM/yyyy')}</strong> è confermata.
        </p>
        <p className="mb-6 font-mono text-sm text-gray-400">
          Ticket: {bookingConfirmed.ticketNumber}
        </p>
        <p className="mb-8 max-w-sm text-sm text-gray-400">
          Riceverai una conferma via email. Il tecnico verrà assegnato nei prossimi giorni.
        </p>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => navigate('/portal/tickets')}>
            Vedi i miei ticket
          </Button>
          <Button onClick={() => navigate('/portal/dashboard')}>
            Dashboard
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Intestazione */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900"
          onClick={() => navigate(-1)}
          aria-label="Torna indietro"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Indietro
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Prenota manutenzione</h1>
          <p className="mt-1 text-sm text-gray-500">
            Seleziona un contratto e scegli la data preferita per l'intervento
          </p>
        </div>
      </div>

      {bookingError && (
        <Alert variant="error">{bookingError}</Alert>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Colonna principale: selettore contratto + calendario */}
        <div className="space-y-6 lg:col-span-2">
          {/* Selezione contratto */}
          <Card>
            <h2 className="mb-4 text-sm font-semibold text-gray-900">
              1. Seleziona il contratto
            </h2>
            {contractsLoading ? (
              <Skeleton height={44} className="rounded-lg" />
            ) : contracts?.length === 0 ? (
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <Info className="h-4 w-4 text-amber-500" aria-hidden="true" />
                <p className="text-sm text-amber-700">
                  Nessun contratto attivo trovato. Contatta il nostro team.
                </p>
              </div>
            ) : (
              <Select
                options={contractOptions}
                value={selectedContractId}
                onValueChange={(v) => {
                  setSelectedContractId(v)
                  setSelectedDate(null)
                }}
                placeholder="Scegli il contratto di manutenzione..."
                label=""
              />
            )}
          </Card>

          {/* Calendario */}
          {selectedContractId ? (
            <Card>
              <h2 className="mb-4 text-sm font-semibold text-gray-900">
                2. Scegli la data
              </h2>
              <SlotCalendar
                slots={slots ?? []}
                currentMonth={currentMonth}
                onMonthChange={setCurrentMonth}
                onSlotSelect={(date) => {
                  setSelectedDate(date)
                }}
                selectedDate={selectedDate}
                isLoading={slotsLoading}
              />
            </Card>
          ) : (
            <div className="flex h-48 items-center justify-center rounded-xl border-2 border-dashed border-gray-200">
              <div className="text-center">
                <Calendar className="mx-auto mb-2 h-10 w-10 text-gray-200" aria-hidden="true" />
                <p className="text-sm text-gray-400">Seleziona prima un contratto</p>
              </div>
            </div>
          )}

          {/* Note */}
          {selectedDate && (
            <Card>
              <h2 className="mb-3 text-sm font-semibold text-gray-900">
                3. Note aggiuntive (opzionale)
              </h2>
              <textarea
                rows={3}
                value={bookingNotes}
                onChange={(e) => setBookingNotes(e.target.value)}
                placeholder="Accesso speciale richiesto, contatto referente, orario preferito..."
                maxLength={1000}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]"
                aria-label="Note per la prenotazione"
              />
            </Card>
          )}
        </div>

        {/* Colonna laterale: riepilogo */}
        <div className="space-y-4">
          <Card className={selectedDate ? 'border-blue-200 bg-blue-50' : ''}>
            <h2 className="mb-4 text-sm font-semibold text-gray-900">
              Riepilogo prenotazione
            </h2>

            {!selectedContractId ? (
              <p className="text-sm text-gray-400">Seleziona un contratto per procedere.</p>
            ) : !selectedDate ? (
              <p className="text-sm text-gray-400">
                Seleziona una data disponibile nel calendario.
              </p>
            ) : (
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                    Data intervento
                  </p>
                  <p className="text-lg font-bold text-[var(--fc-primary)]">
                    {formatDate(selectedDate, 'dd MMMM yyyy')}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                    Contratto
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {contractOptions.find((c) => c.value === selectedContractId)?.label}
                  </p>
                </div>
                {bookingNotes && (
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                      Note
                    </p>
                    <p className="text-sm text-gray-700">{bookingNotes}</p>
                  </div>
                )}

                <Button
                  className="w-full"
                  onClick={() => bookMutation.mutate()}
                  isLoading={bookMutation.isPending}
                  leftIcon={<Calendar className="h-4 w-4" />}
                >
                  Conferma prenotazione
                </Button>
              </div>
            )}
          </Card>

          <Card>
            <div className="flex items-start gap-2">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--fc-primary)]" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-gray-900">Come funziona</p>
                <ul className="mt-2 space-y-1 text-xs text-gray-500">
                  <li>I slot disponibili sono i giorni feriali del mese</li>
                  <li>Il tecnico verrà assegnato dopo la conferma</li>
                  <li>Riceverai un'email di conferma con i dettagli</li>
                  <li>Puoi modificare entro 48h dalla data</li>
                </ul>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
